// Report Templates — customizable completion-report layouts. See
// api/src/lib/reportTemplateEngine.js for the token-resolution engine
// and ReportTemplateEditor.tsx for the authoring UI. The template
// marked "Default" is used everywhere a report is generated without an
// explicit template picked (the single-WO "Download report" button,
// emailed reports, and Data Export's bulk flow when no template is
// chosen there) — falling back to the app's built-in layout when no
// template is marked default at all.

import { useState } from 'react'
import {
  Box, Button, Chip, IconButton, Stack, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, type ReportTemplate } from '@cmms/api-client'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { ReportTemplateEditor } from './components/ReportTemplateEditor'

export function ReportTemplatesTab() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<ReportTemplate | null>(null)
  const confirmDlg = useConfirm()

  const templatesQ = useQuery({
    queryKey: ['report-templates'],
    queryFn: () => api.reportTemplates.list(),
  })
  const templates: ReportTemplate[] = Array.isArray(templatesQ.data) ? templatesQ.data : []

  const setDefaultM = useMutation({
    mutationFn: (id: string) => api.reportTemplates.setDefault(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report-templates'] })
      toast.success('Default template updated')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const deleteM = useMutation({
    mutationFn: (id: string) => api.reportTemplates.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report-templates'] })
      toast.success('Template deleted')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const handleDelete = async (t: ReportTemplate) => {
    const ok = await confirmDlg({
      title: 'Delete this template?',
      message: t.is_default
        ? `"${t.name}" is currently the default — deleting it means reports fall back to the built-in layout until another template is set as default.`
        : `"${t.name}" will be permanently removed.`,
      confirmLabel: 'Delete template',
      variant: 'danger',
    })
    if (ok) deleteM.mutate(t.id)
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}
        justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Report templates</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            Customize the layout of work order / task completion reports.
            The template marked <strong>Default</strong> is used everywhere
            a report is generated unless a different one is explicitly
            picked (Settings → Data Export lets you pick per export).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}>
          New template
        </Button>
      </Stack>

      {templatesQ.isLoading ? (
        <Typography sx={{ color: 'text.disabled', textAlign: 'center', py: 4 }}>Loading…</Typography>
      ) : templates.length === 0 ? (
        <Box sx={{
          textAlign: 'center', py: 6, border: '1px dashed', borderColor: 'divider',
          borderRadius: 2, color: 'text.disabled',
        }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>No custom templates yet</Typography>
          <Typography variant="body2" sx={{ maxWidth: 420, mx: 'auto' }}>
            Reports use the app's built-in layout until you create a
            template and mark it as default.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {templates.map((t) => (
            <Box key={t.id} sx={{
              p: 2, borderRadius: 2, border: '1px solid',
              borderColor: t.is_default ? 'primary.main' : 'divider',
              display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Tooltip title={t.is_default ? 'Default template' : 'Set as default'}>
                <IconButton size="small" onClick={() => !t.is_default && setDefaultM.mutate(t.id)}
                  disabled={setDefaultM.isPending}
                  sx={{ color: t.is_default ? '#F59E0B' : 'text.disabled' }}>
                  {t.is_default ? <StarRoundedIcon /> : <StarOutlineRoundedIcon />}
                </IconButton>
              </Tooltip>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{t.name}</Typography>
                  {t.is_default && (
                    <Chip label="Default" size="small" sx={{
                      height: 20, fontSize: 10.5, fontWeight: 700,
                      bgcolor: '#F59E0B22', color: '#F59E0B',
                    }} />
                  )}
                </Stack>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  Updated {new Date(t.updated_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Tooltip title="Edit template">
                <IconButton size="small" onClick={() => setEditing(t)}>
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete template">
                <IconButton size="small" onClick={() => handleDelete(t)} sx={{ color: '#DC2626' }}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}

      {(createOpen || editing) && (
        <ReportTemplateEditor template={editing}
          onClose={() => { setCreateOpen(false); setEditing(null) }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['report-templates'] })
            setCreateOpen(false); setEditing(null)
          }} />
      )}
    </Box>
  )
}
